/** packages/shared-components/src/components/KeyboardLayoutSelector/KeyboardLayoutContent.tsx */

import { Keyboard } from "@eweadn-qiankun/shared-components";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { KeyPosition } from "../Keyboard/types";
import { KEY_LAYOUT_CATEGORIES } from "./keyboardLayoutData";

export interface KeyboardLayoutContentProps {
  activeCategory: string;
  currentCategoryIndex: number;
  setActiveCategory: (categoryId: string) => void;
  /** 源按键点击选择回调 */
  onSelect?: (keyCode: number) => void;
  /** 禁用的 keyCode 列表（如未配置指令的宏） */
  disabledKeyCodes?: number[];
  /** 下方按键是否可拖拽到上方虚拟键盘改键 */
  draggable?: boolean;
  scrollContainerRef?: React.Ref<HTMLDivElement>;
  /** 左侧点击时由父组件写入待滚动的分组索引；与当前索引一致时消费并 scrollIntoView */
  pendingSidebarScrollToIndexRef: React.MutableRefObject<number | null>;
  /** 用于KeyboardLayoutContent的键盘骨架屏兜底 */
  layoutLoading?: boolean;
}

function getScrollRoot(
  ref: React.Ref<HTMLDivElement> | undefined
): HTMLDivElement | null {
  if (!ref) return null;
  if (typeof ref === "function") return null;
  return ref.current;
}

/** 根据可视区域，取「可见高度最大、其次距视口中心最近」的分组索引 */
function pickVisibleSectionIndex(container: HTMLElement): number {
  const sections = container.querySelectorAll(
    ".keyboard-layout-selector-layout"
  );
  if (sections.length === 0) return 0;

  const containerRect = container.getBoundingClientRect();
  const vpTop = containerRect.top;
  const vpBottom = containerRect.bottom;
  const vpCenter = (vpTop + vpBottom) / 2;

  let bestIdx = 0;
  let bestScore = -Infinity;

  sections.forEach((el, i) => {
    const r = el.getBoundingClientRect();
    const visibleTop = Math.max(r.top, vpTop);
    const visibleBottom = Math.min(r.bottom, vpBottom);
    const visibleH = Math.max(0, visibleBottom - visibleTop);
    const center = (r.top + r.bottom) / 2;
    const dist = Math.abs(center - vpCenter);
    const score = visibleH * 10000 - dist;
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  });

  return bestIdx;
}

const KeyboardLayoutContent: React.FC<KeyboardLayoutContentProps> = ({
  activeCategory,
  currentCategoryIndex,
  setActiveCategory,
  onSelect,
  disabledKeyCodes,
  draggable = true,
  scrollContainerRef,
  pendingSidebarScrollToIndexRef,
  layoutLoading,
}) => {
  const { t } = useTranslation();
  /** 左侧点击触发的 scrollIntoView 过程中，忽略 scroll 同步，避免中间态改类 */
  const programmaticScrollRef = useRef(false);
  const scrollSyncRafRef = useRef<number | null>(null);

  const labelDict: Record<string, string> = {
    "keyboard.keyCategory.basic": t("keyboard.keyCategory.basic"),
    "keyboard.keyCategory.special": t("keyboard.keyCategory.special"),
    "keyboard.keyCategory.media": t("keyboard.keyCategory.media"),
    "keyboard.keyCategory.tool": t("keyboard.keyCategory.tool"),
    "keyboard.keyCategory.light": t("keyboard.keyCategory.light"),
    "keyboard.keyCategory.control": t("keyboard.keyCategory.control"),
    "keyboard.keyCategory.mouse": t("keyboard.keyCategory.mouse"),
    "keyboard.keyCategory.macro": t("keyboard.keyCategory.macro"),
  };
  const getLabel = (key: string | undefined) => {
    if (!key) return "";
    return labelDict[key] ?? key;
  };

  const handleSourceKeySelect = (
    keys: string[],
    positions?: Record<string, KeyPosition>
  ) => {
    const keyCode = keys[0] ? positions?.[keys[0]]?.keyCode : undefined;
    if (typeof keyCode === "number") {
      onSelect?.(keyCode);
    }
  };

  // 左侧点击：仅当 pending 索引与当前分类索引一致时执行 scrollIntoView（滚动同步改类不会写入 pending）
  useEffect(() => {
    const root = getScrollRoot(scrollContainerRef);
    if (!root) return;
    const pendingIdx = pendingSidebarScrollToIndexRef.current;
    if (pendingIdx === null || pendingIdx !== currentCategoryIndex) return;
    pendingSidebarScrollToIndexRef.current = null;

    const sections = root.querySelectorAll(".keyboard-layout-selector-layout");
    const element = sections[pendingIdx] as HTMLElement | undefined;
    if (!element) return;

    programmaticScrollRef.current = true;
    element.scrollIntoView({ behavior: "smooth", block: "nearest" });

    const clearProgrammatic = () => {
      programmaticScrollRef.current = false;
    };
    // smooth 滚动时长不固定，优先用 scrollend；无则定时兜底，避免过早放开导致 scroll 同步抢类
    const fallbackTimer = window.setTimeout(clearProgrammatic, 520);
    const onScrollEnd = () => {
      window.clearTimeout(fallbackTimer);
      clearProgrammatic();
    };
    root.addEventListener("scrollend", onScrollEnd, { once: true });
    return () => {
      window.clearTimeout(fallbackTimer);
      root.removeEventListener("scrollend", onScrollEnd);
    };
  }, [
    currentCategoryIndex,
    pendingSidebarScrollToIndexRef,
    scrollContainerRef,
  ]);

  // 右侧滚动：同步左侧选中
  useEffect(() => {
    const root = getScrollRoot(scrollContainerRef);
    if (!root) return;

    const onScroll = () => {
      if (programmaticScrollRef.current) return;
      if (scrollSyncRafRef.current != null) {
        cancelAnimationFrame(scrollSyncRafRef.current);
      }
      scrollSyncRafRef.current = requestAnimationFrame(() => {
        scrollSyncRafRef.current = null;
        const idx = pickVisibleSectionIndex(root);
        const categories = KEY_LAYOUT_CATEGORIES();
        const cat = categories[idx];
        if (cat && cat.id !== activeCategory) {
          setActiveCategory(cat.id);
        }
      });
    };

    root.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      root.removeEventListener("scroll", onScroll);
      if (scrollSyncRafRef.current != null) {
        cancelAnimationFrame(scrollSyncRafRef.current);
      }
    };
  }, [activeCategory, setActiveCategory, scrollContainerRef]);

  return (
    <div ref={scrollContainerRef} className="keyboard-layout-selector-content">
      {KEY_LAYOUT_CATEGORIES().map((category, index) => (
        <div
          key={category.id}
          className="keyboard-layout-selector-layout"
          style={{
            minHeight: index === 0 ? "360px" : "",
            marginTop: index === 1 ? "24px" : "",
          }}
        >
          <header style={{ marginBottom: "6px", fontSize: "14px" }}>
            {getLabel(category.labelKey)}
          </header>
          <Keyboard
            keyboardType={category.keyboardType || "keymap104"}
            styleMode="neutral"
            selectionMode="single"
            draggable={draggable}
            showLabels
            isExtraKeys={index !== 0}
            keySize={index === 0 ? 48 : 60}
            keyGap={index === 0 ? 4 : 6}
            noBorder
            disabledKeyCodes={disabledKeyCodes}
            layoutLoading={layoutLoading}
            disableViewportFit
            className="keyboard-layout-selector-keyboard"
            onKeySelect={handleSourceKeySelect}
            suppressSelectedKeyStyle
          />
        </div>
      ))}
    </div>
  );
};

export default KeyboardLayoutContent;
